import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const fn = httpsCallable(functions, "finanzasQuestions");

const C = {
  copper: "#b5836d", copperDark: "#8a5d4a",
  dark: "#2d2a26", muted: "#7a756e",
  bg: "#faf8f6", card: "#fff", line: "#ebe5df",
  green: "#4a9c6d", greenBg: "#edf7f0", red: "#c0392b", redBg: "#fdf0ee",
};

const fmtDate = (ms) => ms ? new Date(ms).toLocaleDateString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const sans = "'DM Sans',sans-serif";

function StatusPill({ status }) {
  const answered = status === "answered";
  return (
    <span style={{
      padding: "2px 9px", borderRadius: "12px", fontSize: "10px", fontWeight: 700, fontFamily: sans,
      color: answered ? C.green : C.copperDark, background: answered ? C.greenBg : "#f3e9e3",
    }}>{answered ? "Respondida" : "Pendiente"}</span>
  );
}

export default function Preguntas({ user }) {
  const [tab, setTab] = useState("mias");
  const [mine, setMine] = useState([]);
  const [pub, setPub] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [msg, setMsg] = useState(null);

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([fn({ action: "listMine" }), fn({ action: "listPublic" })]);
      setMine(m.data?.items || []);
      setPub(p.data?.items || []);
    } catch (e) { console.error(e); setMsg({ type: "error", text: "No se pudieron cargar las preguntas." }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);

  const openThread = async (id) => {
    setOpenId(id); setThread(null); setThreadLoading(true); setReply("");
    try {
      const r = await fn({ action: "thread", questionId: id });
      setThread(r.data);
    } catch (e) { console.error(e); setMsg({ type: "error", text: "No se pudo abrir la pregunta." }); setOpenId(null); }
    finally { setThreadLoading(false); }
  };

  const submitNew = async () => {
    if (!text.trim()) { setMsg({ type: "error", text: "Escribí tu pregunta." }); return; }
    setBusy(true); setMsg(null);
    try {
      await fn({ action: "ask", subject: subject.trim(), text: text.trim() });
      setSubject(""); setText(""); setShowForm(false);
      setMsg({ type: "ok", text: "¡Pregunta enviada! Te avisamos por mail cuando te respondan." });
      await loadLists();
    } catch (e) { console.error(e); setMsg({ type: "error", text: e?.message || "No se pudo enviar." }); }
    finally { setBusy(false); }
  };

  const submitReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await fn({ action: "reply", questionId: openId, text: reply.trim() });
      setReply("");
      await openThread(openId);
      await loadLists();
    } catch (e) { console.error(e); setMsg({ type: "error", text: e?.message || "No se pudo enviar la respuesta." }); }
    finally { setBusy(false); }
  };

  const card = { background: C.card, borderRadius: "12px", border: `1px solid ${C.line}`, padding: "16px" };
  const inputBase = {
    width: "100%", padding: "10px 12px", border: `1.5px solid ${C.line}`, borderRadius: "8px",
    fontFamily: sans, fontSize: "14px", color: C.dark, background: "#fdfcfb", outline: "none", boxSizing: "border-box",
  };
  const primaryBtn = (b) => ({
    padding: "10px 18px", border: "none", borderRadius: "8px", cursor: b ? "wait" : "pointer",
    fontFamily: sans, fontSize: "13px", fontWeight: 700,
    background: `linear-gradient(135deg, ${C.copper}, ${C.copperDark})`, color: "#fff", opacity: b ? 0.6 : 1,
  });

  // ===== Vista de hilo =====
  if (openId) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Libre Baskerville','Georgia',serif", padding: "20px" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <button onClick={() => { setOpenId(null); setThread(null); }} style={{
            background: "none", border: "none", cursor: "pointer", color: C.copper, fontFamily: sans, fontSize: "13px", fontWeight: 600, marginBottom: "14px", padding: 0,
          }}>← Volver a mis preguntas</button>

          {threadLoading || !thread ? (
            <div style={{ ...card, textAlign: "center", color: C.muted, fontFamily: sans, fontSize: "13px" }}>Cargando…</div>
          ) : (
            <>
              <div style={{ ...card, marginBottom: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                  <h1 style={{ margin: 0, fontSize: "18px", color: C.dark }}>{thread.question.subject}</h1>
                  <StatusPill status={thread.question.status} />
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "14px" }}>
                {thread.messages.map((m) => {
                  const prof = m.authorType === "professor";
                  return (
                    <div key={m.id} style={{
                      ...card, padding: "12px 14px",
                      background: prof ? C.greenBg : C.card,
                      borderColor: prof ? `${C.green}44` : C.line,
                      marginLeft: prof ? "24px" : 0, marginRight: prof ? 0 : "24px",
                    }}>
                      <div style={{ fontSize: "11px", fontWeight: 700, color: prof ? C.green : C.copperDark, fontFamily: sans, marginBottom: "4px" }}>
                        {prof ? "Profesor" : "Vos"} · <span style={{ color: C.muted, fontWeight: 500 }}>{fmtDate(m.createdAt)}</span>
                      </div>
                      <div style={{ fontFamily: sans, fontSize: "14px", color: C.dark, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{m.text}</div>
                    </div>
                  );
                })}
              </div>

              <div style={card}>
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3}
                  placeholder="Escribí una respuesta o repregunta…" style={{ ...inputBase, resize: "vertical" }} />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                  <button onClick={submitReply} disabled={busy || !reply.trim()} style={{ ...primaryBtn(busy), opacity: busy || !reply.trim() ? 0.5 : 1 }}>Enviar</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ===== Vista de listas =====
  const list = tab === "mias" ? mine : pub;
  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Libre Baskerville','Georgia',serif", padding: "20px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>
      <div style={{ maxWidth: "640px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
          <Link to="/" style={{ color: C.copper, textDecoration: "none", fontFamily: sans, fontSize: "13px", fontWeight: 600 }}>← Inicio</Link>
          <h1 style={{ margin: 0, fontSize: "22px", color: C.dark }}>Preguntas</h1>
          <span style={{ width: "50px" }} />
        </div>

        {msg && (
          <div style={{
            padding: "10px 12px", borderRadius: "8px", marginBottom: "14px", fontFamily: sans, fontSize: "12px", fontWeight: 600,
            background: msg.type === "ok" ? C.greenBg : C.redBg, color: msg.type === "ok" ? C.green : C.red,
          }}>{msg.text}</div>
        )}

        {/* Nueva pregunta */}
        {showForm ? (
          <div style={{ ...card, marginBottom: "14px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: C.copper, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: sans, marginBottom: "10px" }}>Nueva pregunta</div>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Asunto (ej: Duda sobre el food cost)" style={{ ...inputBase, marginBottom: "8px" }} />
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Escribí tu pregunta con el mayor detalle posible…" style={{ ...inputBase, resize: "vertical" }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
              <button onClick={() => { setShowForm(false); setSubject(""); setText(""); }} style={{ padding: "10px 16px", border: `1.5px solid ${C.line}`, borderRadius: "8px", cursor: "pointer", fontFamily: sans, fontSize: "13px", fontWeight: 600, background: "#fff", color: C.muted }}>Cancelar</button>
              <button onClick={submitNew} disabled={busy} style={primaryBtn(busy)}>{busy ? "Enviando…" : "Enviar pregunta"}</button>
            </div>
          </div>
        ) : (
          <button onClick={() => { setShowForm(true); setMsg(null); }} style={{ ...primaryBtn(false), width: "100%", marginBottom: "16px", padding: "12px" }}>+ Hacer una pregunta</button>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
          {[{ k: "mias", label: "Mis preguntas" }, { k: "faq", label: "Preguntas frecuentes" }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} style={{
              padding: "7px 16px", borderRadius: "16px", cursor: "pointer", fontFamily: sans, fontSize: "12px", fontWeight: 700,
              border: `1px solid ${tab === t.k ? C.copper : C.line}`, background: tab === t.k ? C.copper : "#fff", color: tab === t.k ? "#fff" : C.muted,
            }}>{t.label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ ...card, textAlign: "center", color: C.muted, fontFamily: sans, fontSize: "13px" }}>Cargando…</div>
        ) : list.length === 0 ? (
          <div style={{ ...card, textAlign: "center", color: C.muted, fontFamily: sans, fontSize: "13px", padding: "28px" }}>
            {tab === "mias" ? "Todavía no hiciste ninguna pregunta. ¡Animate!" : "Todavía no hay preguntas frecuentes."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {list.map((q) => (
              <button key={q.id} onClick={() => openThread(q.id)} style={{
                ...card, textAlign: "left", cursor: "pointer", border: `1px solid ${C.line}`,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: sans, fontSize: "14px", fontWeight: 600, color: C.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.subject}</div>
                  <div style={{ fontFamily: sans, fontSize: "11px", color: C.muted, marginTop: "2px" }}>
                    {fmtDate(q.lastMessageAt)}{q.messageCount ? ` · ${q.messageCount} mensaje${q.messageCount > 1 ? "s" : ""}` : ""}
                  </div>
                </div>
                {tab === "mias" && <StatusPill status={q.status} />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
